import { urlComSessao } from "./sessao";
import type { Row, DashboardData } from "./types";

const BASE = "/api";

/**
 * Usuário logado nesta sessão. É enviado no header das operações de usuários
 * para o servidor validar que apenas administradores criam/editam/excluem.
 */
let sessionUser = "";
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });
/** Login da sessão — para módulos com cliente próprio (ex.: src/trafos.ts). */
export function usuarioDaSessao(): string {
  return sessionUser;
}

/** Uma observação da célula da agenda (escrita no Planejamento; aqui só leitura). */
export interface Observacao {
  id: number;
  texto: string;
  autor: string | null;
  created_at: string;
}

export class ApiError extends Error {
  code: string;
  constructor(message: string, code = "ERROR") {
    super(message);
    this.code = code;
  }
}

async function handle(res: Response) {
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    let code = "ERROR";
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      if (body?.code) code = body.code;
    } catch {
      /* ignora */
    }
    throw new ApiError(msg, code);
  }
  return res.json();
}

export const api = {
  list: (resource: string): Promise<Row[]> =>
    fetch(`${BASE}/${resource}`, { headers: authHeaders() }).then(handle),

  /**
   * Recados do planejador naquele dia da agenda. Somente leitura: quem escreve
   * é o Planejamento (mesma base). Ver server/agenda-observacoes.ts.
   */
  agendaObservacoes: (agendaId: number): Promise<Observacao[]> =>
    fetch(`${BASE}/agenda/${agendaId}/observacoes`, { headers: authHeaders() }).then(handle),

  create: (resource: string, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  update: (resource: string, id: number, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  remove: (resource: string, id: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(handle),

  dashboard: (): Promise<DashboardData> =>
    fetch(`${BASE}/dashboard`, { headers: authHeaders() }).then(handle),

  /**
   * Veículos que precisam de manutenção (vencida/próxima/em manutenção).
   * Sem argumento: todos (visão de gestão). Passe o colaborador_id do usuário
   * logado para trazer só os veículos sob a responsabilidade dele.
   */
  manutencaoVeiculos: (responsavel?: number | null): Promise<VeiculoManutencao[]> =>
    fetch(
      `${BASE}/manutencao-veiculos${responsavel != null ? `?responsavel=${responsavel}` : ""}`,
      { headers: authHeaders() },
    ).then(handle),

  /** Retorna dados + permissões do usuário. permissoes=null => sem restrição (vê tudo). */
  me: (usuario: string): Promise<{
    usuario: string;
    nome: string | null;
    cargo: string | null;
    isAdmin: boolean;
    /** Só admin do Portal exclui registros — o front esconde os botões quando falso. */
    podeExcluir: boolean;
    permissoes: string[] | null;
    colaborador_id: number | null;
  }> => fetch(`${BASE}/me?u=${encodeURIComponent(usuario)}`).then(handle),

  /** IA de treinamentos: sugestão por colaborador + conclusão (skill/certificado). */
  ia: {
    sugerirTreinamentos: (colaboradorId: number): Promise<SugestaoTreino> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/sugerir-treinamentos`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    aplicarTreinamentos: (colaboradorId: number, treinamentos: Row[]): Promise<{ ok: boolean; criados: number }> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/sugerir-treinamentos/aplicar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ treinamentos }),
      }).then(handle),

    concluirTreinamento: (treinamentoId: number, form: FormData): Promise<{ ok: boolean; certificado: string | null; skill_atualizada: string }> =>
      fetch(`${BASE}/treinamentos/${treinamentoId}/concluir`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
      }).then(handle),
  },

  /** Treinamentos: anexar o certificado (salva na pasta local + envia ao DP). */
  treinamentos: {
    enviarCertificado: (
      treinamentoId: number,
      form: FormData,
    ): Promise<{
      ok: boolean;
      local: string | null;
      dp: { enviado: boolean; mensagem: string; colaborador_id?: number };
    }> =>
      fetch(`${BASE}/treinamentos/${treinamentoId}/certificado`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
      }).then(handle),
  },

  /** Pasta de arquivos/certificados do colaborador. */
  arquivos: {
    list: (colaboradorId: number): Promise<ArquivoInfo[]> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos`, { headers: authHeaders() }).then(handle),

    upload: (colaboradorId: number, files: File[] | FileList): Promise<{ ok: boolean; salvos: string[] }> => {
      const fd = new FormData();
      for (const f of Array.from(files)) fd.append("file", f);
      return fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    url: (colaboradorId: number, nome: string): string =>
      urlComSessao(`${BASE}/colaboradores/${colaboradorId}/arquivos/${encodeURIComponent(nome)}`),

    remove: (colaboradorId: number, nome: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos/${encodeURIComponent(nome)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /** Consulta de insumos e solicitação de separação ao Almoxarifado (via proxy HTTP). */
  almox: {
    listItens: (): Promise<ItemAlmox[]> =>
      fetch(`${BASE}/almoxarifado/itens`, { headers: authHeaders() }).then(handle),

    listSolicitacoes: (solicitante?: string): Promise<SolicitacaoAlmox[]> =>
      fetch(`${BASE}/almoxarifado/solicitacoes${solicitante ? `?solicitante=${encodeURIComponent(solicitante)}` : ""}`, { headers: authHeaders() }).then(handle),

    criarSolicitacao: (data: { item_id: number; quantidade: number; prioridade: string; pedido: string }): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/almoxarifado/solicitacoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),
  },

  /** Folgas: avaliar (gestor), solicitar + minhas (colaborador). */
  folgas: {
    list: (): Promise<Folga[]> =>
      fetch(`${BASE}/folgas`, { headers: authHeaders() }).then(handle),

    avaliar: (id: number, decisao: "Aprovada" | "Reprovada", obs?: string): Promise<Folga> =>
      fetch(`${BASE}/folgas/${id}/avaliar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ decisao, obs: obs ?? "" }),
      }).then(handle),

    /** Solicita uma folga para o colaborador vinculado ao login atual. */
    solicitar: (data: string, motivo: string): Promise<Folga> =>
      fetch(`${BASE}/folgas/solicitar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ data, motivo }),
      }).then(handle),

    /** Folgas do próprio colaborador (para acompanhar o status). */
    minhas: (): Promise<Folga[]> =>
      fetch(`${BASE}/folgas/minhas`, { headers: authHeaders() }).then(handle),
  },

  /**
   * Documentos dos veículos (anexados na Gestão da Operação). Abrir/baixar é
   * pelos links `url`/`url_baixar` de cada documento, já assinados.
   */
  documentosVeiculos: {
    list: (): Promise<VeiculoComDocumentos[]> =>
      fetch(`${BASE}/documentos-veiculos`, { headers: authHeaders() }).then(handle),
  },

  /** Inspeção de veículo (checklist pré-uso): listar + registrar. */
  inspecoes: {
    list: (): Promise<Inspecao[]> =>
      fetch(`${BASE}/inspecoes`, { headers: authHeaders() }).then(handle),

    create: (data: NovaInspecao): Promise<{ id: number; data: string; status: string }> =>
      fetch(`${BASE}/inspecoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    /** Fotos do checklist — só ao abrir os detalhes (a lista não as carrega). */
    fotos: (id: number): Promise<FotoInspecao[]> =>
      fetch(`${BASE}/inspecoes/${id}/fotos`, { headers: authHeaders() }).then(handle),
  },
};

/** Veículo que precisa de manutenção (retorno de /manutencao-veiculos). */
/** Documento de um veículo (sem o arquivo; abrir/baixar pelos links). */
export interface DocumentoVeiculo {
  id: number;
  veiculo_id: number;
  tipo: string;
  descricao: string | null;
  validade: string | null;
  nome_arquivo: string;
  mime: string;
  tamanho: number;
  criado_em: string;
  url: string;
  url_baixar: string;
}

/** Veículo da frota com os dados do cadastro e os documentos dele. */
export interface VeiculoComDocumentos {
  id: number;
  placa: string | null;
  modelo: string | null;
  marca: string | null;
  ano: number | null;
  km_atual: number | null;
  status: string | null;
  responsavel: string | null;
  documentos: DocumentoVeiculo[];
}

export interface VeiculoManutencao {
  id: number;
  placa: string | null;
  modelo: string | null;
  colaborador_id: number | null;
  colaborador_nome: string | null;
  proxima_revisao: string | null;
  data_agendada: string | null;
  situacao_manutencao: string; // Manutenção vencida | Revisão próxima | Em manutenção
}

export interface SugestaoTreino {
  colaborador: { id: number; nome: string; cargo: string | null };
  diagnostico: string;
  fonte: "ia" | "padrao";
  treinamentos: { titulo: string; tipo: string; carga_horaria: number; prioridade: string; justificativa: string }[];
}

export interface ArquivoInfo {
  nome: string;
  tamanho: number;
  enviado_em: string;
  ext: string;
}

export interface ItemAlmox {
  id: number;
  codigo: string;
  descricao: string;
  categoria: string;
  unidade: string;
  localizacao: string;
  saldo: number;
  estoque_minimo: number;
  situacao: string;
  status: string;
}

export interface SolicitacaoAlmox {
  id: number;
  item_id: number;
  item_descricao?: string;
  item_codigo?: string;
  pedido: string;
  solicitante: string;
  quantidade: number;
  data_solicitacao: string;
  data_conclusao: string | null;
  prioridade: string;
  status: string;
}

/** Uma resposta do checklist de inspeção ("pergunta de selecionar"). */
export interface RespostaInspecao {
  item: string;
  resposta: string; // Conforme | Não conforme | N/A
  obs?: string;
  /** Fotos do item, como data URL (só no ENVIO — a listagem não as devolve). */
  fotos?: string[];
}

/** Uma foto do checklist, já pronta para exibir. */
export interface FotoInspecao {
  id: number;
  item_index: number;
  item: string | null;
  url: string; // data:image/jpeg;base64,...
}

/** Payload de criação de uma inspeção de veículo. */
export interface NovaInspecao {
  veiculo_id?: number | null;
  placa?: string;
  modelo?: string;
  km?: number | null;
  data?: string; // YYYY-MM-DD
  respostas: RespostaInspecao[];
  observacoes?: string;
}

/** Inspeção de veículo registrada. */
export interface Inspecao {
  id: number;
  veiculo_id: number | null;
  colaborador_id: number | null;
  colaborador_nome: string | null;
  inspetor: string | null;
  placa: string | null;
  modelo: string | null;
  veiculo_placa: string | null;
  veiculo_modelo: string | null;
  km: number | null;
  data: string;
  respostas: RespostaInspecao[];
  observacoes: string | null;
  status: string; // Conforme | Com pendências
  created_at: string;
  /** Quantas fotos o checklist tem (as imagens vêm por /inspecoes/:id/fotos). */
  fotos_total?: number;
}

/** Folga (alocação da agenda com cor "Folga") a ser avaliada. */
export interface Folga {
  id: number;
  colaborador_id: number | null;
  colaborador_nome: string | null;
  data: string; // YYYY-MM-DD
  servico: string | null;
  cor: string;
  folga_status: "Aprovada" | "Reprovada" | null; // null = pendente
  folga_obs: string | null;
  folga_avaliador: string | null;
  folga_avaliado_em: string | null;
}
